/**
 * Search-engine facing facts: the public origin, the two language versions of
 * every URL and the title / description of each indexable page. Used by the
 * Worker (per-page <head> tags, language redirect) and by the sitemap test.
 *
 * URL scheme: Turkish at the root (/, /computer, …), English under /en
 * (/en, /en/computer, …). Each page links to the other with hreflang.
 */

export const SITE_NAME = 'Chessrokko';
export const SITE_ORIGIN = 'https://chessrokko.com';
export const SITE_HOST = 'chessrokko.com';

export type SeoLocale = 'tr' | 'en';

/** Remembers an explicit language choice so the country redirect leaves it alone. */
export const LANG_COOKIE = 'lang';

export function localeOfPath(path: string): SeoLocale {
  return path === '/en' || path.startsWith('/en/') ? 'en' : 'tr';
}

/** The path without its language prefix ("/en/market" → "/market"). */
export function stripLocale(path: string): string {
  return path.replace(/^\/en(?=\/|$)/, '') || '/';
}

/** The same page in `locale` ("/market", "en" → "/en/market"). */
export function localizePath(path: string, locale: SeoLocale): string {
  const bare = stripLocale(path);
  if (locale === 'tr') return bare;
  return bare === '/' ? '/en' : `/en${bare}`;
}

interface PageText {
  title: string;
  description: string;
}

/** Pages worth showing in search results, with their text in both languages. */
export const INDEXABLE_PAGES: Record<string, Record<SeoLocale, PageText>> = {
  '/': {
    tr: {
      title: 'Chessrokko · Ücretsiz Online Satranç Oyna',
      description:
        'Arkadaşınla ya da dünyanın dört bir yanındaki oyuncularla online satranç oyna, acemiden ustaya sekiz seviyeli bilgisayara karşı pratik yap. Ücretsiz ve çocuklara uygun.',
    },
    en: {
      title: 'Chessrokko · Play Free Chess Online',
      description:
        'Play chess online with friends or players around the world, and practise against the computer at eight levels, from beginner to master. Free and kid-friendly.',
    },
  },
  '/online': {
    tr: {
      title: 'Online satranç oyna · Chessrokko',
      description: 'Saniyeler içinde bir rakiple eşleş. Giriş yaparak puan, altın ve rozet kazan; liderler tablosunda yüksel.',
    },
    en: {
      title: 'Play chess online · Chessrokko',
      description: 'Get matched with an opponent in seconds. Sign in to earn rating points, gold and badges, and climb the leaderboard.',
    },
  },
  '/computer': {
    tr: {
      title: 'Bilgisayara karşı satranç · Chessrokko',
      description: 'Acemiden ustaya sekiz seviyeli bilgisayara karşı satranç oyna. İpucu al, hamle geri al, kendi hızında öğren.',
    },
    en: {
      title: 'Play chess against the computer · Chessrokko',
      description: 'Play eight computer opponents, from beginner to master. Get hints, take back moves and learn at your own pace.',
    },
  },
  '/friend': {
    tr: {
      title: 'Arkadaşınla satranç oyna · Chessrokko',
      description: 'Bir oda oluştur, bağlantıyı arkadaşına gönder ve hemen birlikte satranç oyna. Üyelik gerekmez.',
    },
    en: {
      title: 'Play chess with a friend · Chessrokko',
      description: 'Create a room, send your friend the link and play chess together right away. No sign-up needed.',
    },
  },
  '/leaderboard': {
    tr: {
      title: 'Liderler tablosu · Chessrokko',
      description: 'Günün, haftanın ve tüm zamanların en iyi satranç oyuncuları. İlk üçe gir, hediye kutusu kazan.',
    },
    en: {
      title: 'Leaderboard · Chessrokko',
      description: 'The best chess players of the day, the week and all time. Finish in the top three to win a gift box.',
    },
  },
  '/market': {
    tr: {
      title: 'Market · Chessrokko',
      description: 'Oyunlarda kazandığın altınlarla avatar, çerçeve ve isim süsleri al.',
    },
    en: {
      title: 'Shop · Chessrokko',
      description: 'Spend the gold you win in games on avatars, frames and name styles.',
    },
  },
};

INDEXABLE_PAGES['/how-to-play'] = {
  tr: {
    title: 'Satranç nasıl oynanır? Kurallar ve ipuçları · Chessrokko',
    description: 'Satranç kuralları adım adım: taşların hareketi, rok, terfi, geçerken alma, şah, mat ve pat. Yeni başlayanlar ve çocuklar için ipuçları.',
  },
  en: {
    title: 'How to play chess: rules and tips · Chessrokko',
    description: 'Chess rules step by step: how the pieces move, castling, promotion, en passant, check, checkmate and stalemate, plus tips for beginners and kids.',
  },
};

INDEXABLE_PAGES['/about'] = {
  tr: {
    title: 'Hakkında · Chessrokko',
    description: 'Chessrokko, her yaştan oyuncu için, özellikle çocuklar düşünülerek yapılmış ücretsiz ve güvenli bir online satranç sitesidir.',
  },
  en: {
    title: 'About · Chessrokko',
    description: 'Chessrokko is a free, safe online chess site for players of all ages, designed especially for children who are starting out.',
  },
};

INDEXABLE_PAGES['/privacy'] = {
  tr: {
    title: 'Gizlilik politikası · Chessrokko',
    description: 'Chessrokko hangi bilgileri neden saklar, çerezler, ziyaret istatistikleri, reklamlar ve çocukların gizliliği hakkında sade bir açıklama.',
  },
  en: {
    title: 'Privacy policy · Chessrokko',
    description: 'What Chessrokko keeps and why, cookies, visitor statistics, ads and children’s privacy, explained in plain words.',
  },
};

/** Personal or one-off pages that should never appear in search results. */
export function isPrivatePath(bare: string): boolean {
  return bare === '/me' || bare === '/gifts' || bare === '/computer/play' || bare.startsWith('/r/');
}

export interface PageSeo {
  locale: SeoLocale;
  title: string;
  description: string;
  canonical: string;
  alternates: { hreflang: string; href: string }[];
  indexable: boolean;
}

/** Everything the <head> needs for one URL path. */
export function pageSeo(path: string): PageSeo {
  const locale = localeOfPath(path);
  const bare = stripLocale(path);
  const page = INDEXABLE_PAGES[bare] ?? INDEXABLE_PAGES['/'];
  const profile = /^\/u\/([A-Za-z0-9_]{1,20})$/.exec(bare)?.[1];
  const text = profile
    ? {
        title: `${profile} · ${SITE_NAME}`,
        description:
          locale === 'tr'
            ? `${profile} adlı oyuncunun ${SITE_NAME} profili: puanı, rozetleri ve istatistikleri.`
            : `${profile}'s ${SITE_NAME} profile: rating, badges and stats.`,
      }
    : page[locale];
  const url = (p: string) => `${SITE_ORIGIN}${p === '/' ? '/' : p}`;
  return {
    locale,
    ...text,
    canonical: url(localizePath(bare, locale)),
    alternates: [
      { hreflang: 'tr', href: url(localizePath(bare, 'tr')) },
      { hreflang: 'en', href: url(localizePath(bare, 'en')) },
      { hreflang: 'x-default', href: url(localizePath(bare, 'tr')) },
    ],
    indexable: !isPrivatePath(bare) && (bare in INDEXABLE_PAGES || Boolean(profile)),
  };
}

/** Search engine and link-preview crawlers: never redirected by country, so both languages get indexed. */
export function isCrawler(userAgent: string | null): boolean {
  return /bot|crawl|spider|slurp|facebookexternalhit|whatsapp|telegram|discord|preview|lighthouse/i.test(userAgent ?? '');
}

/** Countries whose visitors get Turkish by default; everyone else gets English. */
export const TURKISH_COUNTRIES = new Set(['TR', 'CY']);
